import React, { useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import TRToast from '../../../_shared/helpers/TRToast';
import Alert from '../../../_shared/components/Alert';
import {
  EditInfoButton,
  FieldDealerName,
  FieldDescription,
  FieldGroup,
  ModalBoxContentWrapper,
  ModalHeader,
  ModalOuterBox,
  ModalOuterBoxContainer,
  StyledLogoContainer,
  StyledLogoGroup,
  StyledLogoPreview,
  StyledNoteGroup,
  StyledSubTitle
} from '../Styles';
import addPhotoBrowse from '../../../assets/external/add-photo-browse.png';
import RetailToolsEditModal from '../RetailToolsEditModal';
import SettingsService from '../../../services/SettingsService';
import MixpanelService from '../../../services/MixpanelService';
import mixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import InputFieldFormatting from '../../../_shared/helpers/InputFieldFormatting';

const RetailToolsDealerLogo = ({
  testId,
  companyName,
  imageSrc,
  isOmp,
  customizationData,
  dealerData
}) => {
  const [showModal, setShowModal] = useState(false);
  const [imgSrc, setImgSrc] = useState(imageSrc ?? null);
  const [fileObj, setFileObj] = useState(null);
  const imgRef = useRef(null);
  const maxFileSize = 15000000;
  const settingsService = SettingsService.get();
  const mixpanelService = MixpanelService.get();

  async function handleLogoSave() {
    const requestBody = {
      ...dealerData,
      dealerLogoSrc: imgSrc
        ? InputFieldFormatting.formatS3ImageSrc(imgSrc)
        : null
    };

    const modifiedFields = {
      dealer_logo: false
    };
    const fileType = fileObj.type.substring(fileObj.type.indexOf('/') + 1);
    const presignedUrl = await settingsService.getPresignedUrl(fileType);
    if (!presignedUrl?.data || !presignedUrl?.data?.presignedS3Uris) {
      throw new Error('Failed to generate presigned URL');
    }
    try {
      const logoS3Uri = await SettingsService.uploadFile(
        presignedUrl?.data?.presignedS3Uris,
        fileObj,
        fileType
      );

      requestBody.dealerLogoSrc = InputFieldFormatting.formatS3ImageSrc(
        logoS3Uri
      );
      const updateResponse = await settingsService.updateDealerRetailInfo(
        requestBody
      );
      if (updateResponse?.isError) {
        throw new Error(updateResponse?.errorMessage);
      }
      modifiedFields.dealer_logo = true;
      mixpanelService.track(
        mixpanelConfig.names.retailSettingsFieldsUpdated,
        modifiedFields
      );
      TRToast(
        <Alert
          variant="success"
          messages={[
            <FormattedMessage id="retail.tools.setting.confirmation" />
          ]}
          isOmp={isOmp}
        />
      );
    } catch (e) {
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="retail.tools.setting.upload.logo.error" />
          ]}
          isOmp={isOmp}
        />
      );
      throw e;
    }
  }

  function toggleShowModal() {
    setShowModal(!showModal);
  }
  const removeLogo = () => {
    setImgSrc(null);
    setFileObj(null);
  };
  const handleImageLoad = () => {
    if (
      imgRef?.current?.naturalWidth < 500 ||
      imgRef?.current?.naturalHeight < 500
    ) {
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="retail.tools.setting.logo.upload.error" />
          ]}
          isOmp={isOmp}
        />
      );
      setImgSrc(null);
      setFileObj(null);
    }
  };
  const addLogo = e => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (file[0].size < maxFileSize) {
      const blobSrc = window.URL.createObjectURL(file[0]);
      setImgSrc(blobSrc);
      setFileObj(file[0]);
    } else {
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="retail.tools.setting.logo.upload.error" />
          ]}
          isOmp={isOmp}
        />
      );
      setImgSrc(null);
      setFileObj(null);
    }
  };

  return (
    <React.Fragment key="logo">
      <ModalOuterBox isOmp={isOmp}>
        <ModalBoxContentWrapper>
          <ModalOuterBoxContainer>
            <StyledSubTitle>
              <FormattedMessage id="retail.tools.setting.heading" />
              <FieldDealerName>{companyName}</FieldDealerName>
            </StyledSubTitle>
            <FieldDescription>
              <FormattedMessage id="retail.tools.setting.heading.description" />
            </FieldDescription>
            <>
              <FieldGroup>
                {imgSrc ? (
                  <FormattedMessage id="retail.tools.setting.edit.logo" />
                ) : (
                  <FormattedMessage id="retail.tools.setting.upload.logo" />
                )}
              </FieldGroup>
              <FieldDescription data-testid={`${testId}-uploadLogo`}>
                <FormattedMessage id="retail.tools.setting.upload.logo.description" />
              </FieldDescription>
            </>
            <EditInfoButton
              testId={`${testId}-logo`}
              onClick={() => toggleShowModal()}
            >
              <FormattedMessage
                id={
                  imgSrc
                    ? 'retail.tools.setting.edit.logo'
                    : 'list.filters.add.logo'
                }
              />
            </EditInfoButton>
          </ModalOuterBoxContainer>
        </ModalBoxContentWrapper>
      </ModalOuterBox>
      <RetailToolsEditModal
        testId={`${testId}-uploadModal-image`}
        isModalOpen={showModal}
        isOmp={isOmp}
        closeHandler={() => {
          setShowModal(false);
        }}
        saveHandler={async () => {
          await handleLogoSave();
          setShowModal(false);
        }}
        disableSaveCondition={false}
      >
        <ModalHeader>
          <span>
            <FormattedMessage
              id={
                imgSrc
                  ? 'retail.tools.setting.edit.logo.for'
                  : 'retail.tools.setting.upload.logo.for'
              }
            />
            {` ${companyName}`}
          </span>
        </ModalHeader>

        <StyledLogoGroup>
          {imgSrc ? (
            <>
              <StyledLogoGroup.StyledRemoveIcon
                data-testid={`${testId}-removeLogo`}
                clickHandler={() => removeLogo()}
              />
              <StyledLogoContainer>
                <StyledLogoPreview
                  alt="logo preview"
                  src={imgSrc}
                  ref={imgRef}
                  onLoad={handleImageLoad}
                />
              </StyledLogoContainer>
            </>
          ) : (
            <StyledLogoGroup.ModalLogoInputLabel>
              <StyledLogoGroup.StyledBrowseImage
                alt="upload logo"
                src={addPhotoBrowse}
              />
              <StyledLogoGroup.LogoInput
                type="file"
                name="logo"
                accept="image/png, image/jpg, image/jpeg"
                ref={customizationData.ref}
                onChange={addLogo}
                data-testid={`${testId}-uploadLogo`}
              />
              <StyledLogoGroup.StyledAddIcon />
            </StyledLogoGroup.ModalLogoInputLabel>
          )}
          <StyledNoteGroup>
            <StyledNoteGroup.StyledNote>
              <FormattedMessage
                id="retail.tools.setting.logo.note"
                values={{
                  b: chunks => <b>{chunks}</b>
                }}
              />
            </StyledNoteGroup.StyledNote>
            <StyledNoteGroup.StyledNote>
              <FormattedMessage id="retail.tools.setting.logo.size" />
            </StyledNoteGroup.StyledNote>
          </StyledNoteGroup>
        </StyledLogoGroup>
      </RetailToolsEditModal>
    </React.Fragment>
  );
};

export default RetailToolsDealerLogo;
